/**
 * Pure helpers for the constellation's blast-set lookups.
 */
import type { TwinEdge, TwinNode, TwinNodeKind } from "shared/api/types";

/** Sort nodes deterministically for layout: by kind tier then name. */
const KIND_TIER: Record<TwinNodeKind, number> = {
  channel: 0,
  ibs: 1,
  service: 2,
  vendor: 3,
};

export function sortNodes(nodes: TwinNode[]): TwinNode[] {
  return [...nodes].sort((a, b) => {
    const dt = KIND_TIER[a.kind] - KIND_TIER[b.kind];
    if (dt !== 0) return dt;
    return a.name.localeCompare(b.name);
  });
}

/**
 * Build the dependents map: dependents[x] = node ids that
 * (directly) depend on x downstream.
 *
 * Used to highlight the "if X fails, who's affected" set on the
 * canvas without needing a backend round-trip for visual feedback.
 * (The authoritative reached-set still comes from
 * /v1/twin/blast-graph/{kind}/{id}; this client-side walk is for
 * the hover-affordance fast path.)
 */
export function buildDependents(edges: TwinEdge[]): Map<string, string[]> {
  const out = new Map<string, string[]>();
  for (const e of edges) {
    // Direction in our schema: from depends_on/hosted_by/supports to.
    // Downstream of `to` includes `from`. The constellation's
    // semantics: failing a node ripples to its dependents -- the
    // nodes that need this one.
    let key: string;
    let dependent: string;
    if (e.relation === "supports") {
      // channel "supports" ibs -- failing the ibs ripples to the
      // channel that surfaces it.
      key = e.to_id;
      dependent = e.from_id;
    } else if (e.relation === "hosted_by") {
      // service "hosted_by" vendor -- failing the vendor ripples
      // to the service it hosts.
      key = e.to_id;
      dependent = e.from_id;
    } else {
      // depends_on (ibs->service, service->parent_service) +
      // linked_to fallback. failing the "to" ripples to the "from".
      key = e.to_id;
      dependent = e.from_id;
    }
    if (!out.has(key)) out.set(key, []);
    out.get(key)!.push(dependent);
  }
  return out;
}

/**
 * BFS over `dependents` from a seed node id. Returns the set of
 * reachable downstream node ids (EXCLUDING the seed itself --
 * matches the backend's TwinBlastGraph.reached_node_ids contract).
 *
 * Used by the SVG renderer to dim non-blast nodes when a seed is
 * selected, without waiting for the server-side blast endpoint to
 * resolve.
 */
export function reachableFrom(
  seedId: string,
  dependents: Map<string, string[]>,
): Set<string> {
  const seen = new Set<string>();
  // `visited` includes the seed; `seen` (returned) does not. On a CYCLE
  // the walk loops back to the seed, and without this it would report
  // the seed as reachable from itself -- breaking the documented
  // contract. Same defect fixed server-side in P385.2.
  const visited = new Set<string>([seedId]);
  const stack: string[] = [seedId];
  while (stack.length > 0) {
    const cur = stack.pop()!;
    const next = dependents.get(cur) ?? [];
    for (const n of next) {
      if (visited.has(n)) continue;
      visited.add(n);
      seen.add(n);
      stack.push(n);
    }
  }
  return seen;
}

// ── P385: bidirectional trace ───────────────────────────────────────

/**
 * Build the upstream map: upstream[x] = node ids x DEPENDS ON.
 *
 * The exact inverse of `buildDependents`, so the client mirrors the
 * server's `invert_adjacency`. Downstream answers "if this fails, who
 * is hurt"; upstream answers "what must come back before this can" --
 * the prerequisite direction the P366 recovery sequencer schedules on.
 */
export function buildUpstream(edges: TwinEdge[]): Map<string, string[]> {
  const out = new Map<string, string[]>();
  for (const e of edges) {
    // Every relation in the schema runs `from` depends on `to`, so the
    // upstream of `from` includes `to`.
    if (!out.has(e.from_id)) out.set(e.from_id, []);
    out.get(e.from_id)!.push(e.to_id);
  }
  return out;
}

/**
 * BFS from a seed returning HOP DEPTH per reached node (1 = direct
 * neighbour). Mirrors the server's `bfs_reach_with_depth`.
 *
 * The seed is excluded from the result. Tracking it as visited is
 * load-bearing on a CYCLE: without it the walk loops back and reports
 * the seed as reachable from ITSELF. The estate has such a cycle
 * (AuthHost + T24 + PaymentsHub + mada, P366), and the same defect was
 * found and fixed server-side in P385.2.
 */
export function reachableWithDepth(
  seedId: string,
  adjacency: Map<string, string[]>,
): Map<string, number> {
  const depth = new Map<string, number>();
  const visited = new Set<string>([seedId]);
  let frontier = [seedId];
  let d = 0;

  while (frontier.length > 0) {
    d += 1;
    const next: string[] = [];
    for (const cur of frontier) {
      for (const n of adjacency.get(cur) ?? []) {
        if (visited.has(n)) continue;
        visited.add(n);
        depth.set(n, d);
        next.push(n);
      }
    }
    frontier = next;
  }
  return depth;
}

/** Greatest hop distance in a trace, or 0 when nothing was reached. */
export function maxDepth(...traces: Map<string, number>[]): number {
  let m = 0;
  for (const t of traces) for (const v of t.values()) if (v > m) m = v;
  return m;
}
